import { Divider, Stack, Typography } from '@mui/material';
import {
    BooleanInput,
    ReferenceInput,
    required,
    SelectInput,
    TextInput,
    useRecordContext,
    useGetList,
} from 'react-admin';
import { antennaOptionText } from '@app/antenna/AntennaInputOption';
import { AnimationLightCard } from '@app/animation/show/AnimationLightCard';
import ImageEditorField from '@app/misc/ImageEditorField';
import { UrlInput } from '@app/misc/UrlInput';
import { useResource } from '@app/misc/useResource';
import type { OrganizationInput } from '@domain/organization';
import { AnimationLight } from '@wwf/sdk';

const OrganizationNameAndLogoInputs = () => {
    const record = useRecordContext<OrganizationInput>();
    const { data: logoSrc } = useResource(record?.logoId);

    return (
        <Stack gap={2} direction="row" flexWrap="wrap" alignItems="flex-start">
            <ImageEditorField
                source="logoInput"
                height={60}
                emptyText={
                    record?.name?.trim()?.charAt(0)?.toUpperCase() || '?'
                }
                linkPosition="bottom"
                src={logoSrc ?? undefined}
            />
            <TextInput
                source="name"
                label="Organization name"
                validate={required()}
                helperText={false}
                sx={{ flex: 1, minWidth: 200, mt: 0 }}
            />
        </Stack>
    );
};

const OrganizationChatAvatarAndNameInputs = () => {
    const record = useRecordContext<OrganizationInput>();
    const { data: avatarSrc } = useResource(record?.chatbotAssistantAvatarId);

    return (
        <Stack gap={2} direction="row" flexWrap="wrap" alignItems="flex-start">
            <ImageEditorField
                source="chatbotAssistantAvatarInput"
                type="avatar"
                height={56}
                width={56}
                emptyText={
                    record?.chatbotAssistantName
                        ?.trim()
                        ?.charAt(0)
                        ?.toUpperCase() ?? '?'
                }
                linkPosition="bottom"
                src={avatarSrc ?? undefined}
            />
            <TextInput
                source="chatbotAssistantName"
                label="Assistant name"
                helperText={false}
                sx={{ flex: 1, minWidth: 200, mt: 0 }}
            />
        </Stack>
    );
};

const OrganizationMascotHugImageInput = () => {
    const record = useRecordContext<OrganizationInput>();
    const { data: mascotSrc } = useResource(record?.mascotHugImageId);

    return (
        <ImageEditorField
            source="mascotHugImageInput"
            height={100}
            width={100}
            linkPosition="bottom"
            src={mascotSrc ?? undefined}
        />
    );
};

export const OrganizationInputs = () => {
    const { data: animations = [] } = useGetList<AnimationLight>('animation', {
        pagination: { page: 0, perPage: 100 },
        sort: { field: 'name', order: 'ASC' },
        filter: { enabled: true },
    });
    return (
        <Stack gap={4} p={1}>
            <Stack gap={1}>
                <Typography variant="h6">General</Typography>
                <OrganizationNameAndLogoInputs />
            </Stack>

            <Divider />

            <Stack gap={1}>
                <Typography variant="h6">Chatbot</Typography>
                <OrganizationChatAvatarAndNameInputs />
                <TextInput
                    source="chatbotAssistantDescription"
                    label="Assistant description"
                    multiline
                    minRows={3}
                    fullWidth
                    helperText={
                        'In english as the assistant instructions are in english, but he gonna speak in the language of the user.'
                    }
                />
                <TextInput
                    source="chatbotOrganizationDescription"
                    label="Organization description"
                    multiline
                    minRows={3}
                    fullWidth
                    helperText={
                        'In english as the assistant instructions are in english, but he gonna speak in the language of the user.'
                    }
                />
            </Stack>

            <Divider />

            <Stack gap={1}>
                <Typography variant="h6">Links</Typography>
                <UrlInput
                    source="driveResourcesLink"
                    label="Drive resources link"
                    helperText={false}
                />
                <UrlInput
                    source="mainVolunteersCommunityWhatsappLink"
                    label="Main volunteers community WhatsApp link"
                    helperText={false}
                />
            </Stack>

            <Divider />

            <Stack gap={1}>
                <Typography variant="h6">References</Typography>
                <ReferenceInput
                    source="antennaHeadquarterId"
                    reference="antenna"
                    sort={{ field: 'name', order: 'ASC' }}
                >
                    <SelectInput
                        optionText={antennaOptionText}
                        label="Antenna headquarter"
                        helperText="Headquarter antenna"
                    />
                </ReferenceInput>
                <SelectInput
                    source="mascotAnimationId"
                    label="Mascot panda animation"
                    choices={animations}
                    optionText={<AnimationLightCard noLink forceAvailable />}
                    helperText="Animation for the mascot panda"
                />
                <Typography variant="body2" color="text.secondary">
                    Mascot hug image
                </Typography>
                <OrganizationMascotHugImageInput />
            </Stack>

            <Divider />

            <Stack gap={1}>
                <Typography variant="h6">Features (Main volunteers)</Typography>
                <BooleanInput
                    source="enableWelcomePhraseForMainVolunteers"
                    label="Enable welcome phrase"
                    defaultValue={true}
                />
                <BooleanInput
                    source="enableChatbotForMainVolunteers"
                    label="Enable chatbot"
                    defaultValue={true}
                />
            </Stack>

            <Stack gap={1}>
                <Typography variant="h6">Features (Volunteers)</Typography>
                <BooleanInput
                    source="enableWelcomePhraseForVolunteers"
                    label="Enable welcome phrase"
                    defaultValue={true}
                />
                <BooleanInput
                    source="enableChatbotForVolunteers"
                    label="Enable chatbot"
                    defaultValue={true}
                />
            </Stack>

            <Stack gap={1}>
                <Typography variant="h6">Features (Staff)</Typography>
                <BooleanInput
                    source="enableWelcomePhraseForStaff"
                    label="Enable welcome phrase"
                    defaultValue={true}
                />
                <BooleanInput
                    source="enableChatbotForStaff"
                    label="Enable chatbot"
                    defaultValue={true}
                />
            </Stack>
        </Stack>
    );
};
